import { Injectable } from '@angular/core';
import { DynamicSettingsButtonType } from 'node_js/models/enums/dynamic-settings-button-type';
import { LabControllerInputType } from 'node_js/models/enums/lab-controller-input-type';
import { TUTORIAL_STEP_DETAILS } from './tutorial/tutorial-step-details';
import { LanguageService } from './language.service';
import { TutorialId, TutorialSheetPosition, TutorialState, TutorialStepDetails, TutorialStepId } from './tutorial/tutorial.models';

export type { TutorialId, TutorialSheetPosition, TutorialState, TutorialStepDetails, TutorialStepId } from './tutorial/tutorial.models';

@Injectable({
  providedIn: 'root'
})
export class TutorialService {

  private state: TutorialState = {
    tutorialId: null,
    stepId: null,
    justCompletedTutorialId: null,
  };
  private overlayPausedUntil = 0;
  private createLabNameReady = false;
  private createGadgetNameReady = false;
  private createComponentNameReady = false;
  private dashboardControllerNameReady = false;
  private componentGuideConditionSummary = '';
  private componentGuideHasOtherComponents = true;
  private componentGuideAffectedComponentName = '';
  private componentGuideAffectedComponentValue = '0';
  private resumeDashboardControllerAfterCreateGadget = false;
  private resumeDashboardControllerAfterCreateComponent = false;
  private dashboardControllerCanCreateGadgetInLab = false;
  private dashboardControllerGadgetOptions: { id: string; name: string; }[] = [];
  private selectedDashboardControllerGadgetId: string | null = null;
  private dashboardControllerTutorialTestComponentId: string | null = null;
  private dashboardControllerSelectionWarning = '';
  private dashboardControllerTryButtonUsed = false;
  private dashboardControllerButtonType: DynamicSettingsButtonType = DynamicSettingsButtonType.Push;

  constructor(private languageService: LanguageService) {}

  private get isMobileDashboardViewport(): boolean {
    return window.innerWidth <= 1024;
  }

  private t(value: string, params: any[] = []) {
    if (params.length > 0) {
      return this.languageService.getTranslationWithParams(value || '', params, false);
    }
    return this.languageService.translateUiLiteral(value || '');
  }

  private resetComponentGuideContext() {
    this.componentGuideConditionSummary = '';
    this.componentGuideHasOtherComponents = true;
    this.componentGuideAffectedComponentName = '';
    this.componentGuideAffectedComponentValue = '0';
  }

  private startTutorial(tutorialId: TutorialId, stepId: TutorialStepId) {
    if (tutorialId === 'component-guide') {
      this.resetComponentGuideContext();
    }

    this.createLabNameReady = false;
    this.createGadgetNameReady = false;
    this.createComponentNameReady = false;
    this.dashboardControllerNameReady = false;

    if (tutorialId !== 'dashboard-controller') {
      this.dashboardControllerCanCreateGadgetInLab = false;
      this.dashboardControllerGadgetOptions = [];
      this.selectedDashboardControllerGadgetId = null;
      this.dashboardControllerTutorialTestComponentId = null;
      this.dashboardControllerSelectionWarning = '';
      this.dashboardControllerTryButtonUsed = false;
      this.dashboardControllerButtonType = DynamicSettingsButtonType.Push;
    }

    this.state = {
      tutorialId,
      stepId,
      justCompletedTutorialId: null,
    };
  }

  private setStep(stepId: TutorialStepId) {
    if (this.state.tutorialId === 'dashboard-controller' && this.state.stepId !== stepId) {
      this.dashboardControllerSelectionWarning = '';
    }

    this.state = {
      ...this.state,
      stepId,
    };
  }

  get activeTutorialId(): TutorialId | null {
    return this.state.tutorialId;
  }

  get justCompletedTutorialId(): TutorialId | null {
    return this.state.justCompletedTutorialId;
  }

  get activeStepId(): TutorialStepId | null {
    return this.state.stepId;
  }

  get isTutorialActive(): boolean {
    return !!this.state.tutorialId && !!this.state.stepId;
  }

  get isCreateLabTutorialActive(): boolean {
    return this.state.tutorialId === 'create-lab';
  }

  get isLabMembersTutorialActive(): boolean {
    return this.state.tutorialId === 'lab-members';
  }

  get isCreateGadgetTutorialActive(): boolean {
    return this.state.tutorialId === 'create-gadget';
  }

  get isGadgetUsersTutorialActive(): boolean {
    return this.state.tutorialId === 'gadget-users';
  }

  get isCreateComponentTutorialActive(): boolean {
    return this.state.tutorialId === 'create-component';
  }

  get isComponentGuideTutorialActive(): boolean {
    return this.state.tutorialId === 'component-guide';
  }

  get isDashboardControllerTutorialActive(): boolean {
    return this.state.tutorialId === 'dashboard-controller';
  }

  get isAddContactTutorialActive(): boolean {
    return this.state.tutorialId === 'add-contact';
  }

  get shouldResumeDashboardControllerAfterCreateGadget(): boolean {
    return this.resumeDashboardControllerAfterCreateGadget;
  }

  get shouldResumeDashboardControllerAfterCreateComponent(): boolean {
    return this.resumeDashboardControllerAfterCreateComponent;
  }

  get createGadgetCompletionContinueLabel(): string {
    return this.t(this.resumeDashboardControllerAfterCreateGadget
      ? 'Continue to Create Component'
      : 'Continue to Gadget Members');
  }

  get createComponentCompletionContinueLabel(): string {
    return this.t('Continue to Component Guide');
  }

  get shouldShowCompletionCard(): boolean {
    return !!this.state.justCompletedTutorialId;
  }

  get activeStepDetails(): TutorialStepDetails | null {
    if (!this.state.tutorialId || !this.state.stepId) {
      return null;
    }

    return TUTORIAL_STEP_DETAILS[this.state.stepId] || null;
  }

  get activeStepTitle(): string {
    if (this.state.tutorialId === 'dashboard-controller'
      && this.state.stepId === 'dashboard-controller-try-button'
      && this.isMobileDashboardViewport) {
      return this.t('Try out the Button and Analog inputs');
    }

    return this.t(this.activeStepDetails?.title || '');
  }

  get activeStepBody(): string {
    let body = this.activeStepDetails?.body || '';

    if (this.state.tutorialId === 'dashboard-controller') {
      switch (this.state.stepId) {
        case 'dashboard-controller-needs-gadget':
          body = this.dashboardControllerCanCreateGadgetInLab
            ? (this.activeStepDetails?.body || '')
            : 'This lab does not currently have a gadget you can build in, and you do not have Creator access in the lab. A gadget must be created first by someone with the correct permissions before the dashboard tutorial can continue.';
          break;
        case 'dashboard-controller-needs-component': {
          const selectedGadgetName = this.dashboardControllerGadgetOptions.find(
            gadget => gadget.id === this.selectedDashboardControllerGadgetId
          )?.name;
          body = selectedGadgetName
            ? this.t('Controllers are built around components, so this tutorial cannot continue until {{param1}} has at least one component you can access. Pick the gadget below, then jump into Create Component, and we will bring you back here afterward.', [selectedGadgetName])
            : (this.activeStepDetails?.body || '');
          break;
        }
        case 'dashboard-controller-try-button': {
          if (this.isMobileDashboardViewport) {
            body = this.t('Play around with the Button and Analog stick to see how they control your dashboard. Tap Next when you are ready to continue.');
            break;
          }

          const selectedTypeLabel = this.t(this.dashboardControllerButtonType === DynamicSettingsButtonType.Toggle ? 'Toggle' : 'Push');
          const behaviorSummary = this.t(this.dashboardControllerButtonType === DynamicSettingsButtonType.Toggle
            ? 'Each press flips between your off and on values.'
            : 'Holding it sends your on value, and releasing it sends your off value.');
          body = this.t('You chose {{param1}} for this Button. {{param2}} Press it now and watch the Text card update with the same component value. Tap Next when you are done playing with it.', [selectedTypeLabel, behaviorSummary]);
          break;
        }
      }
    }
    else if (this.state.tutorialId === 'component-guide') {
      switch (this.state.stepId) {
        case 'component-guide-value-trigger-condition':
          body = this.componentGuideConditionSummary || this.activeStepDetails?.body || '';
          break;
        case 'component-guide-value-trigger-effect-change-value':
          if (this.componentGuideHasOtherComponents) {
            const componentLabel = this.componentGuideAffectedComponentName
              ? ` (${this.componentGuideAffectedComponentName})`
              : '';
            body = this.t('If this trigger fires, the component selected in the dropdown{{param1}} will be set to the value set in the right input ({{param2}}).', [componentLabel, this.componentGuideAffectedComponentValue || '0']);
            break;
          }

          body = this.componentGuideConditionSummary
            ? this.t('{{param1}} There are no other components in this lab yet, so this dropdown will stay empty until you add another component.', [this.componentGuideConditionSummary])
            : (this.activeStepDetails?.body || '');
          break;
        case 'component-guide-value-trigger-effect-push':
          body = this.componentGuideConditionSummary
            ? this.t('{{param1}} Instead of changing another component, you can switch to Push Notification and send a message with live tokens like {componentName} and {componentValue}.', [this.componentGuideConditionSummary])
            : (this.activeStepDetails?.body || '');
          break;
      }
    }

    return this.t(body);
  }

  get activeStepWarning(): string {
    if (this.state.tutorialId === 'dashboard-controller' && [
      'dashboard-controller-connect-text',
      'dashboard-controller-connect-button',
      'dashboard-controller-connect-analog',
    ].includes(this.state.stepId || '')) {
      return this.t(this.dashboardControllerSelectionWarning);
    }

    return '';
  }

  get activeStepProgressLabel(): string {
    return this.t(this.activeStepDetails?.progressLabel || '');
  }

  get activeTargetSelector(): string | null {
    return this.activeStepDetails?.targetSelector || null;
  }

  get activeStepActionLabel(): string {
    let actionLabel = this.activeStepDetails?.actionLabel || '';

    if (this.state.stepId === 'dashboard-controller-needs-gadget') {
      actionLabel = this.dashboardControllerCanCreateGadgetInLab
        ? (this.activeStepDetails?.actionLabel || '')
        : '';
    }
    else if (this.state.stepId === 'dashboard-controller-needs-component') {
      actionLabel = this.dashboardControllerGadgetOptions.length > 0
        ? (this.activeStepDetails?.actionLabel || '')
        : '';
    }

    return this.t(actionLabel);
  }

  get hasActiveStepAction(): boolean {
    return !!this.activeStepActionLabel;
  }

  get isActiveStepActionEnabled(): boolean {
    switch (this.state.stepId) {
      case 'create-lab-name':
        return this.createLabNameReady;
      case 'create-gadget-name':
        return this.createGadgetNameReady;
      case 'create-component-name':
        return this.createComponentNameReady;
      case 'dashboard-controller-name':
        return this.dashboardControllerNameReady;
      case 'dashboard-controller-try-button':
        return this.isMobileDashboardViewport || this.dashboardControllerTryButtonUsed;
      case 'contact-info-nickname':
      case 'contact-info-method':
        return true;
      default:
        return true;
    }
  }

  get shouldShowDashboardControllerGadgetPicker(): boolean {
    return this.state.stepId === 'dashboard-controller-needs-component' && this.dashboardControllerGadgetOptions.length > 0;
  }

  get dashboardControllerStepGadgetOptions(): { id: string; name: string; }[] {
    return this.dashboardControllerGadgetOptions;
  }

  get dashboardControllerSelectedGadgetId(): string {
    return this.selectedDashboardControllerGadgetId || '';
  }

  get dashboardControllerTestComponentId(): string | null {
    return this.dashboardControllerTutorialTestComponentId;
  }

  get allowActiveTargetInteraction(): boolean {
    return this.activeStepDetails?.allowTargetInteraction !== false;
  }

  get activeSheetPosition(): TutorialSheetPosition {
    return this.activeStepDetails?.sheetPosition || 'auto';
  }

  get isOverlayPaused(): boolean {
    return Date.now() < this.overlayPausedUntil;
  }

  get completionTitle(): string {
    let title = '';

    switch (this.state.justCompletedTutorialId) {
      case 'create-lab':
        title = 'Your first lab is ready';
        break;
      case 'lab-members':
        title = 'Lab roles are mapped out';
        break;
      case 'create-gadget':
        title = 'Your first gadget is ready';
        break;
      case 'gadget-users':
        title = 'Gadget roles are mapped out';
        break;
      case 'create-component':
        title = 'Your first component is ready';
        break;
      case 'component-guide':
        title = 'Component automation basics are mapped out';
        break;
      case 'dashboard-controller':
        title = 'Your first dashboard controller is ready';
        break;
      case 'add-contact':
        title = 'Contact request sent';
        break;
    }

    return this.t(title);
  }

  get completionBody(): string {
    let body = '';

    switch (this.state.justCompletedTutorialId) {
      case 'create-lab':
        body = 'Nice. This lab is now live, and you have seen where settings and quick join live. The next segment should take you straight into Lab Members.';
        break;
      case 'lab-members':
        body = 'Nice. Roles are in place. The next useful segment should take you straight into creating your first gadget.';
        break;
      case 'create-gadget':
        body = this.resumeDashboardControllerAfterCreateGadget
          ? 'Your first gadget now lives inside the lab. Next, we will move straight into Create Component so the dashboard tutorial has something to build around.'
          : 'Your first gadget now lives inside the lab. The next useful segment is learning gadget members before adding components.';
        break;
      case 'gadget-users':
        body = 'Nice. Gadget member roles are clear. The next useful segment is creating your first component.';
        break;
      case 'create-component':
        body = 'Your first component is now live inside the gadget. Next, we will show how to edit it so the important settings are easier to understand before you build a dashboard controller around it.';
        break;
      case 'component-guide':
        body = 'Nice. You have seen where component values, MQTT topics, scheduled events, and trigger rules are configured. Next, we will head into the dashboard tutorial.';
        break;
      case 'dashboard-controller':
        body = 'Nice. You built a controller with Text, Button, and Analog, then tried them live. When you are ready, explore the rest of the dashboard input types from the editor.';
        break;
      case 'add-contact':
        body = 'Nice. You have sent a contact request. Once they accept, they will show up in your contacts list.';
        break;
    }

    return this.t(body);
  }

  startCreateLabTutorial() {
    this.startTutorial('create-lab', 'labs-start');
  }

  pauseOverlayFor(durationMs: number) {
    this.overlayPausedUntil = Math.max(this.overlayPausedUntil, Date.now() + Math.max(0, durationMs));
  }

  startLabMembersTutorial() {
    this.startTutorial('lab-members', 'lab-members-help');
  }

  startCreateGadgetTutorial() {
    this.resumeDashboardControllerAfterCreateGadget = false;
    this.resumeDashboardControllerAfterCreateComponent = false;
    this.startTutorial('create-gadget', 'lab-gadgets-start');
  }

  startGadgetUsersTutorial() {
    this.startTutorial('gadget-users', 'gadget-users-help');
  }

  startCreateComponentTutorial() {
    this.resumeDashboardControllerAfterCreateGadget = false;
    this.resumeDashboardControllerAfterCreateComponent = false;
    this.startTutorial('create-component', 'gadget-components-start');
  }

  startComponentGuideTutorial() {
    this.startTutorial('component-guide', 'component-guide-edit');
  }

  startDashboardControllerTutorial(options: {
    hasGadgets: boolean;
    hasComponents: boolean;
    canCreateGadgetInLab: boolean;
    gadgetOptions: { id: string; name: string; }[];
  }) {
    this.dashboardControllerCanCreateGadgetInLab = options.canCreateGadgetInLab;
    this.dashboardControllerGadgetOptions = options.gadgetOptions || [];
    this.selectedDashboardControllerGadgetId = this.dashboardControllerGadgetOptions[0]?.id || null;
    this.dashboardControllerTutorialTestComponentId = null;
    this.dashboardControllerSelectionWarning = '';
    this.dashboardControllerTryButtonUsed = false;
    this.dashboardControllerButtonType = DynamicSettingsButtonType.Push;

    this.startTutorial(
      'dashboard-controller',
      !options.hasGadgets
        ? 'dashboard-controller-needs-gadget'
        : options.hasComponents
          ? 'dashboard-controller-create'
          : 'dashboard-controller-needs-component'
    );
  }

  startAddContactTutorial(isAlreadyOnContactsPage: boolean = false) {
    const startStepId = isAlreadyOnContactsPage ? 'contacts-add-trigger' : 'contacts-start';
    this.startTutorial('add-contact', startStepId);
  }

  startCreateGadgetTutorialFromDashboardController() {
    this.resumeDashboardControllerAfterCreateGadget = true;
    this.resumeDashboardControllerAfterCreateComponent = false;
    this.startTutorial('create-gadget', 'lab-gadgets-start');
  }

  selectDashboardControllerGadget(gadgetId: string) {
    if (!gadgetId) {
      return;
    }

    const gadgetExists = this.dashboardControllerGadgetOptions.some(gadget => gadget.id === gadgetId);
    if (!gadgetExists) {
      return;
    }

    this.selectedDashboardControllerGadgetId = gadgetId;
  }

  noteDashboardControllerButtonTypeSelected(buttonType: DynamicSettingsButtonType | string | null | undefined) {
    if (buttonType === DynamicSettingsButtonType.Toggle) {
      this.dashboardControllerButtonType = DynamicSettingsButtonType.Toggle;
      return;
    }

    this.dashboardControllerButtonType = DynamicSettingsButtonType.Push;
  }

  noteDashboardControllerSelectionWarning(message: string) {
    this.dashboardControllerSelectionWarning = message || '';
  }

  startCreateComponentTutorialFromDashboardController() {
    this.resumeDashboardControllerAfterCreateGadget = false;
    this.resumeDashboardControllerAfterCreateComponent = true;
    this.startTutorial('create-component', 'gadget-components-start');
  }

  resumeDashboardControllerTutorialAfterCreateComponent() {
    this.resumeDashboardControllerAfterCreateComponent = false;
    this.startTutorial('dashboard-controller', 'dashboard-controller-create');
  }

  cancelActiveTutorial() {
    this.resetComponentGuideContext();
    if (this.state.tutorialId === 'create-gadget') {
      this.resumeDashboardControllerAfterCreateGadget = false;
    }
    if (this.state.tutorialId === 'create-component' || this.state.tutorialId === 'component-guide') {
      this.resumeDashboardControllerAfterCreateComponent = false;
    }
    this.state = {
      ...this.state,
      tutorialId: null,
      stepId: null,
    };
    this.overlayPausedUntil = 0;
    this.dashboardControllerTutorialTestComponentId = null;
    this.dashboardControllerSelectionWarning = '';
  }

  dismissCompletion() {
    if (this.state.justCompletedTutorialId === 'create-gadget') {
      this.resumeDashboardControllerAfterCreateGadget = false;
    }
    if (this.state.justCompletedTutorialId === 'create-component' || this.state.justCompletedTutorialId === 'component-guide') {
      this.resumeDashboardControllerAfterCreateComponent = false;
    }
    if (this.state.justCompletedTutorialId === 'dashboard-controller') {
      this.dashboardControllerTutorialTestComponentId = null;
      this.dashboardControllerSelectionWarning = '';
    }

    this.state = {
      ...this.state,
      justCompletedTutorialId: null,
    };
  }

  isStepActive(stepId: TutorialStepId): boolean {
    return this.state.stepId === stepId && !!this.state.tutorialId;
  }

  openCreateLabTutorialStep() {
    if (!this.isCreateLabTutorialActive) {
      return;
    }

    this.setStep('new-lab-create-intro');
  }

  advanceCreateLabIntroToJoin() {
    if (!this.isCreateLabTutorialActive || this.state.stepId !== 'new-lab-create-intro') {
      return;
    }

    this.setStep('new-lab-join-intro');
  }

  advanceCreateLabIntroToClone() {
    if (!this.isCreateLabTutorialActive || this.state.stepId !== 'new-lab-join-intro') {
      return;
    }

    this.setStep('new-lab-clone-intro');
  }

  finishCreateLabIntro() {
    if (!this.isCreateLabTutorialActive || this.state.stepId !== 'new-lab-clone-intro') {
      return;
    }

    this.setStep('create-lab-name');
  }

  skipCreateLabCloneIntro() {
    if (!this.isCreateLabTutorialActive || this.state.stepId !== 'new-lab-clone-intro') {
      return;
    }

    this.setStep('create-lab-name');
  }

  noteLabNameEntered(labName: string) {
    if (!this.isCreateLabTutorialActive) {
      return;
    }

    const trimmedName = labName?.trim() || '';
    this.createLabNameReady = !!trimmedName;

    if (!trimmedName && this.state.stepId === 'create-lab-open-icon-selector') {
      this.setStep('create-lab-name');
    }
  }

  advanceCreateLabNameStep() {
    if (!this.isCreateLabTutorialActive || this.state.stepId !== 'create-lab-name' || !this.createLabNameReady) {
      return;
    }

    this.setStep('create-lab-open-icon-selector');
  }

  noteCreateLabIconPickerOpened() {
    if (!this.isCreateLabTutorialActive || this.state.stepId !== 'create-lab-open-icon-selector') {
      return;
    }

    this.setStep('create-lab-upload-custom');
  }

  advanceCreateLabToCuratedIcons() {
    if (!this.isCreateLabTutorialActive || this.state.stepId !== 'create-lab-upload-custom') {
      return;
    }

    this.setStep('create-lab-select-curated');
  }

  noteCreateLabCuratedIconSelected() {
    if (!this.isCreateLabTutorialActive) {
      return;
    }

    if (this.state.stepId !== 'create-lab-select-curated' && this.state.stepId !== 'create-lab-upload-custom') {
      return;
    }

    this.setStep('create-lab-finish');
  }

  noteCreateLabCreated() {
    if (!this.isCreateLabTutorialActive || this.state.stepId !== 'create-lab-finish') {
      return;
    }

    this.setStep('lab-settings-open');
  }

  noteLabSettingsOpened() {
    if (!this.isCreateLabTutorialActive || this.state.stepId !== 'lab-settings-open') {
      return;
    }

    this.setStep('lab-settings-edit-info');
  }

  advanceLabSettingsToQuickJoin() {
    if (!this.isCreateLabTutorialActive || this.state.stepId !== 'lab-settings-edit-info') {
      return;
    }

    this.setStep('lab-settings-quick-join-code');
  }

  advanceLabSettingsQuickJoinStep() {
    if (!this.isCreateLabTutorialActive) {
      return;
    }

    switch (this.state.stepId) {
      case 'lab-settings-quick-join-code':
        this.setStep('lab-settings-quick-join-copy');
        return;
      case 'lab-settings-quick-join-copy':
        this.setStep('lab-settings-quick-join-refresh');
        return;
      default:
        return;
    }
  }

  noteLabSettingsClosed() {
    if (!this.isCreateLabTutorialActive) {
      return;
    }

    if (![
      'lab-settings-edit-info',
      'lab-settings-quick-join-code',
      'lab-settings-quick-join-copy',
      'lab-settings-quick-join-refresh',
    ].includes(this.state.stepId || '')) {
      return;
    }

    this.setStep('lab-settings-open');
  }

  finishCreateLabSettingsGuide() {
    if (!this.isCreateLabTutorialActive || this.state.stepId !== 'lab-settings-quick-join-refresh') {
      return;
    }

    this.completeActiveTutorial('create-lab');
  }

  noteCreateLabDialogClosed(createdLab: boolean) {
    if (!this.isCreateLabTutorialActive) {
      return;
    }

    if (createdLab) {
      return;
    }

    this.setStep('labs-start');
  }

  noteRoleHelpOpened() {
    if (this.isLabMembersTutorialActive && this.state.stepId === 'lab-members-help') {
      this.setStep('lab-members-role-help');
      return;
    }

    if (this.isGadgetUsersTutorialActive && this.state.stepId === 'gadget-users-help') {
      this.setStep('gadget-users-role-help');
    }
  }

  noteRoleHelpClosed() {
    if (this.isLabMembersTutorialActive && this.state.stepId === 'lab-members-role-help') {
      this.completeActiveTutorial('lab-members');
      return;
    }

    if (this.isGadgetUsersTutorialActive && this.state.stepId === 'gadget-users-role-help') {
      this.completeActiveTutorial('gadget-users');
    }
  }

  openCreateGadgetTutorialStep() {
    if (!this.isCreateGadgetTutorialActive) {
      return;
    }

    this.setStep('create-gadget-name');
  }

  noteGadgetNameEntered(gadgetName: string) {
    if (!this.isCreateGadgetTutorialActive) {
      return;
    }

    const trimmedName = gadgetName?.trim() || '';
    this.createGadgetNameReady = !!trimmedName;

    if (!trimmedName && this.state.stepId === 'create-gadget-open-icon-selector') {
      this.setStep('create-gadget-name');
    }
  }

  advanceCreateGadgetNameStep() {
    if (!this.isCreateGadgetTutorialActive || this.state.stepId !== 'create-gadget-name' || !this.createGadgetNameReady) {
      return;
    }

    this.setStep('create-gadget-open-icon-selector');
  }

  noteCreateGadgetIconPickerOpened() {
    if (!this.isCreateGadgetTutorialActive || this.state.stepId !== 'create-gadget-open-icon-selector') {
      return;
    }

    this.setStep('create-gadget-upload-custom');
  }

  advanceCreateGadgetToCuratedIcons() {
    if (!this.isCreateGadgetTutorialActive || this.state.stepId !== 'create-gadget-upload-custom') {
      return;
    }

    this.setStep('create-gadget-select-curated');
  }

  noteCreateGadgetCuratedIconSelected() {
    if (!this.isCreateGadgetTutorialActive) {
      return;
    }

    if (this.state.stepId !== 'create-gadget-select-curated' && this.state.stepId !== 'create-gadget-upload-custom') {
      return;
    }

    this.setStep('create-gadget-finish');
  }

  noteCreateGadgetCreated() {
    if (!this.isCreateGadgetTutorialActive || this.state.stepId !== 'create-gadget-finish') {
      return;
    }

    this.setStep('gadget-settings-gear');
  }

  acknowledgeCreateGadgetSettings() {
    if (!this.isCreateGadgetTutorialActive || this.state.stepId !== 'gadget-settings-gear') {
      return;
    }

    this.completeActiveTutorial('create-gadget');
  }

  noteCreateGadgetDialogClosed(createdGadget: boolean) {
    if (!this.isCreateGadgetTutorialActive) {
      return;
    }

    if (createdGadget) {
      return;
    }

    this.setStep('lab-gadgets-start');
  }

  openCreateComponentTutorialStep() {
    if (!this.isCreateComponentTutorialActive) {
      return;
    }

    this.setStep('create-component-name');
  }

  noteComponentNameEntered(componentName: string) {
    if (!this.isCreateComponentTutorialActive) {
      return;
    }

    const trimmedName = componentName?.trim() || '';
    this.createComponentNameReady = !!trimmedName;

    if (!trimmedName && this.state.stepId === 'create-component-open-icon-selector') {
      this.setStep('create-component-name');
    }
  }

  advanceCreateComponentNameStep() {
    if (!this.isCreateComponentTutorialActive || this.state.stepId !== 'create-component-name' || !this.createComponentNameReady) {
      return;
    }

    this.setStep('create-component-open-icon-selector');
  }

  noteCreateComponentIconPickerOpened() {
    if (!this.isCreateComponentTutorialActive || this.state.stepId !== 'create-component-open-icon-selector') {
      return;
    }

    this.setStep('create-component-upload-custom');
  }

  advanceCreateComponentToCuratedIcons() {
    if (!this.isCreateComponentTutorialActive || this.state.stepId !== 'create-component-upload-custom') {
      return;
    }

    this.setStep('create-component-select-curated');
  }

  noteCreateComponentCuratedIconSelected() {
    if (!this.isCreateComponentTutorialActive) {
      return;
    }

    if (this.state.stepId !== 'create-component-select-curated' && this.state.stepId !== 'create-component-upload-custom') {
      return;
    }

    this.setStep('create-component-finish');
  }

  noteCreateComponentDialogClosed(createdComponent: boolean) {
    if (!this.isCreateComponentTutorialActive) {
      return;
    }

    if (createdComponent) {
      return;
    }

    this.setStep('gadget-components-start');
  }

  completeCreateComponentTutorial() {
    this.completeActiveTutorial('create-component');
  }

  openDashboardControllerTutorialStep() {
    if (!this.isDashboardControllerTutorialActive || this.state.stepId !== 'dashboard-controller-create') {
      return;
    }

    this.setStep('dashboard-controller-name');
  }

  noteDashboardControllerNameEntered(controllerName: string) {
    if (!this.isDashboardControllerTutorialActive) {
      return;
    }

    const trimmedName = controllerName?.trim() || '';
    this.dashboardControllerNameReady = !!trimmedName;

    if (!trimmedName && this.state.stepId === 'dashboard-controller-finish') {
      this.setStep('dashboard-controller-name');
    }
  }

  advanceDashboardControllerNameStep() {
    if (!this.isDashboardControllerTutorialActive || this.state.stepId !== 'dashboard-controller-name' || !this.dashboardControllerNameReady) {
      return;
    }

    this.setStep('dashboard-controller-finish');
  }

  noteDashboardControllerDialogClosed(createdController: boolean) {
    if (!this.isDashboardControllerTutorialActive) {
      return;
    }

    if (createdController) {
      this.setStep('dashboard-controller-open-editor');
      return;
    }

    if (this.state.stepId === 'dashboard-controller-name' || this.state.stepId === 'dashboard-controller-finish') {
      this.setStep('dashboard-controller-create');
    }
  }

  noteDashboardControllerEditorOpened() {
    if (!this.isDashboardControllerTutorialActive || this.state.stepId !== 'dashboard-controller-open-editor') {
      return;
    }

    this.setStep('dashboard-controller-add-text');
  }

  noteDashboardControllerInputAdded(inputType: LabControllerInputType) {
    if (!this.isDashboardControllerTutorialActive) {
      return;
    }

    switch (this.state.stepId) {
      case 'dashboard-controller-add-text':
        if (inputType === LabControllerInputType.Text) {
          this.setStep('dashboard-controller-select-text');
        }
        return;
      case 'dashboard-controller-add-button':
        if (inputType === LabControllerInputType.Button) {
          this.setStep('dashboard-controller-select-button');
        }
        return;
      case 'dashboard-controller-add-analog':
        if (inputType === LabControllerInputType.Analog) {
          this.setStep('dashboard-controller-select-analog');
        }
        return;
      default:
        return;
    }
  }

  noteDashboardControllerInputSelected(inputType: LabControllerInputType) {
    if (!this.isDashboardControllerTutorialActive) {
      return;
    }

    switch (this.state.stepId) {
      case 'dashboard-controller-select-text':
        if (inputType === LabControllerInputType.Text) {
          this.setStep('dashboard-controller-open-text-input-tab');
        }
        return;
      case 'dashboard-controller-select-button':
        if (inputType === LabControllerInputType.Button) {
          this.setStep('dashboard-controller-open-button-input-tab');
        }
        return;
      case 'dashboard-controller-select-graph':
        if (inputType === LabControllerInputType.Graph) {
          this.setStep('dashboard-controller-connect-graph');
        }
        return;
      case 'dashboard-controller-select-analog':
        if (inputType === LabControllerInputType.Analog) {
          this.setStep('dashboard-controller-open-analog-input-tab');
        }
        return;
      case 'dashboard-controller-select-slider':
        if (inputType === LabControllerInputType.Slider) {
          this.setStep('dashboard-controller-connect-slider');
        }
        return;
      case 'dashboard-controller-select-toggle':
        if (inputType === LabControllerInputType.Toggle) {
          this.setStep('dashboard-controller-connect-toggle');
        }
        return;
      case 'dashboard-controller-select-gauge':
        if (inputType === LabControllerInputType.Gauge) {
          this.setStep('dashboard-controller-connect-gauge');
        }
        return;
      case 'dashboard-controller-select-warning':
        if (inputType === LabControllerInputType.Warning) {
          this.setStep('dashboard-controller-connect-warning');
        }
        return;
      case 'dashboard-controller-select-message':
        if (inputType === LabControllerInputType.Message) {
          this.setStep('dashboard-controller-connect-message');
        }
        return;
      default:
        return;
    }
  }

  noteDashboardControllerInputTabOpened(inputType: LabControllerInputType) {
    if (!this.isDashboardControllerTutorialActive) {
      return;
    }

    switch (this.state.stepId) {
      case 'dashboard-controller-open-text-input-tab':
        if (inputType === LabControllerInputType.Text) {
          this.setStep('dashboard-controller-connect-text');
        }
        return;
      case 'dashboard-controller-open-button-input-tab':
        if (inputType === LabControllerInputType.Button) {
          this.setStep('dashboard-controller-connect-button');
        }
        return;
      case 'dashboard-controller-open-analog-input-tab':
        if (inputType === LabControllerInputType.Analog) {
          this.setStep('dashboard-controller-connect-analog');
        }
        return;
      default:
        return;
    }
  }

  noteDashboardControllerInputConnected(inputType: LabControllerInputType, componentId: string) {
    if (!this.isDashboardControllerTutorialActive || !componentId) {
      return;
    }

    switch (this.state.stepId) {
      case 'dashboard-controller-connect-text':
        if (inputType === LabControllerInputType.Text) {
          this.dashboardControllerTutorialTestComponentId = componentId;
          this.setStep('dashboard-controller-add-button');
        }
        return;
      case 'dashboard-controller-connect-button':
        if (inputType === LabControllerInputType.Button) {
          this.setStep('dashboard-controller-button-mode');
        }
        return;
      case 'dashboard-controller-connect-analog':
        if (inputType === LabControllerInputType.Analog) {
          this.setStep('dashboard-controller-analog-values');
        }
        return;
      default:
        return;
    }
  }

  advanceDashboardControllerConnectedInputStep() {
    return;
  }

  advanceDashboardControllerConfiguredInputStep() {
    if (!this.isDashboardControllerTutorialActive) {
      return;
    }

    switch (this.state.stepId) {
      case 'dashboard-controller-button-mode':
        this.setStep('dashboard-controller-button-values');
        return;
      case 'dashboard-controller-button-values':
        this.setStep('dashboard-controller-add-analog');
        return;
      case 'dashboard-controller-analog-values':
        this.setStep('dashboard-controller-save');
        return;
      default:
        return;
    }
  }

  advanceDashboardControllerPracticeStep() {
    if (!this.isDashboardControllerTutorialActive) {
      return;
    }

    switch (this.state.stepId) {
      case 'dashboard-controller-try-button':
        if (this.isMobileDashboardViewport) {
          this.setStep('dashboard-controller-quick-mode');
        } else if (this.dashboardControllerTryButtonUsed) {
          this.setStep('dashboard-controller-try-analog');
        }
        return;
      default:
        return;
    }
  }

  noteDashboardControllerSaved() {
    if (!this.isDashboardControllerTutorialActive || this.state.stepId !== 'dashboard-controller-save') {
      return;
    }

    this.dashboardControllerTryButtonUsed = false;
    this.setStep('dashboard-controller-try-button');
  }

  noteDashboardControllerInputUsed(inputType: LabControllerInputType) {
    if (!this.isDashboardControllerTutorialActive) {
      return;
    }

    switch (this.state.stepId) {
      case 'dashboard-controller-try-button':
        if (inputType === LabControllerInputType.Button) {
          this.dashboardControllerTryButtonUsed = true;
        }
        return;
      case 'dashboard-controller-try-analog':
        if (inputType === LabControllerInputType.Analog) {
          this.setStep('dashboard-controller-quick-mode');
        }
        return;
      default:
        return;
    }
  }

  completeDashboardControllerTutorial() {
    if (!this.isDashboardControllerTutorialActive || this.state.stepId !== 'dashboard-controller-quick-mode') {
      return;
    }

    this.completeActiveTutorial('dashboard-controller');
  }

  setComponentGuideContext(conditionSummary: string, hasOtherComponents: boolean, affectedComponentName: string = '', affectedComponentValue: string = '0') {
    this.componentGuideConditionSummary = conditionSummary || '';
    this.componentGuideHasOtherComponents = hasOtherComponents;
    this.componentGuideAffectedComponentName = affectedComponentName || '';
    this.componentGuideAffectedComponentValue = affectedComponentValue || '0';
  }

  noteComponentGuideEditingOpened() {
    if (!this.isComponentGuideTutorialActive || this.state.stepId !== 'component-guide-edit') {
      return;
    }

    this.setStep('component-guide-data-type');
  }

  advanceComponentGuide() {
    if (!this.isComponentGuideTutorialActive) {
      return;
    }

    switch (this.state.stepId) {
      case 'component-guide-data-type':
        this.setStep('component-guide-current-value');
        return;
      case 'component-guide-current-value':
        this.setStep('component-guide-mqtt-topic');
        return;
      case 'component-guide-mqtt-topic':
        this.setStep('component-guide-value-triggers');
        return;
      case 'component-guide-value-triggers':
        this.setStep('component-guide-value-trigger-add');
        return;
      case 'component-guide-value-trigger-condition':
        this.setStep('component-guide-value-trigger-effect-add');
        return;
      case 'component-guide-value-trigger-effect-type':
        this.setStep('component-guide-value-trigger-effect-change-value');
        return;
      case 'component-guide-value-trigger-effect-change-value':
        this.setStep('component-guide-value-trigger-effect-push');
        return;
      case 'component-guide-value-trigger-effect-push':
        this.setStep('component-guide-schedules');
        return;
      case 'component-guide-schedules':
        this.setStep('component-guide-schedule-add');
        return;
      case 'component-guide-schedule-frequency':
        this.setStep('component-guide-schedule-next-date');
        return;
      case 'component-guide-schedule-next-date':
        this.setStep('component-guide-schedule-weeks');
        return;
      case 'component-guide-schedule-weeks':
        this.setStep('component-guide-schedule-months');
        return;
      case 'component-guide-schedule-months':
        this.setStep('component-guide-schedule-effects');
        return;
      case 'component-guide-schedule-effects':
        this.setStep('component-guide-finish');
        return;
      default:
        return;
    }
  }

  noteComponentGuideValueTriggerAdded() {
    if (!this.isComponentGuideTutorialActive || this.state.stepId !== 'component-guide-value-trigger-add') {
      return;
    }

    this.setStep('component-guide-value-trigger-condition');
  }

  noteComponentGuideTriggerEffectAdded() {
    if (!this.isComponentGuideTutorialActive || this.state.stepId !== 'component-guide-value-trigger-effect-add') {
      return;
    }

    this.setStep('component-guide-value-trigger-effect-type');
  }

  noteComponentGuideScheduleAdded() {
    if (!this.isComponentGuideTutorialActive || this.state.stepId !== 'component-guide-schedule-add') {
      return;
    }

    this.setStep('component-guide-schedule-frequency');
  }

  completeComponentGuideTutorial() {
    if (!this.isComponentGuideTutorialActive || this.state.stepId !== 'component-guide-finish') {
      return;
    }

    this.completeActiveTutorial('component-guide');
  }

  noteContactsOpened() {
    if (!this.isAddContactTutorialActive || this.state.stepId !== 'contacts-start') {
      return;
    }

    this.setStep('contacts-add-trigger');
  }

  noteAddContactTriggered() {
    if (!this.isAddContactTutorialActive || this.state.stepId !== 'contacts-add-trigger') {
      return;
    }

    this.setStep('contact-info-nickname');
  }

  noteContactNicknameEntered() {
    if (!this.isAddContactTutorialActive || this.state.stepId !== 'contact-info-nickname') {
      return;
    }
    // We could add a check for name length if we wanted to be strict
  }

  advanceAddContactNicknameStep() {
    if (!this.isAddContactTutorialActive || this.state.stepId !== 'contact-info-nickname') {
      return;
    }

    this.setStep('contact-info-method');
  }

  advanceAddContactMethodStep() {
    if (!this.isAddContactTutorialActive || this.state.stepId !== 'contact-info-method') {
      return;
    }

    // Default to email if they just click next without picking
    this.setStep('contact-info-email');
  }

  noteContactMethodSelected(method: 'email' | 'code') {
    if (!this.isAddContactTutorialActive || this.state.stepId !== 'contact-info-method') {
      return;
    }

    if (method === 'email') {
      this.setStep('contact-info-email');
    } else {
      this.setStep('contact-info-code');
    }
  }

  noteContactEmailEntered() {
    if (!this.isAddContactTutorialActive || this.state.stepId !== 'contact-info-email') {
      return;
    }

    this.setStep('contact-info-finish');
  }

  advanceAddContactEmailStep() {
    this.noteContactEmailEntered();
  }

  noteContactCodeEntered() {
    if (!this.isAddContactTutorialActive || this.state.stepId !== 'contact-info-code') {
      return;
    }

    this.setStep('contact-info-finish');
  }

  advanceAddContactCodeStep() {
    this.noteContactCodeEntered();
  }

  noteContactRequestSent() {
    if (!this.isAddContactTutorialActive || this.state.stepId !== 'contact-info-finish') {
      return;
    }

    this.setStep('navbar-profile-trigger');
  }

  advanceAddContactFinishStep() {
    this.noteContactRequestSent();
  }

  noteProfileOpened() {
    if (!this.isAddContactTutorialActive || this.state.stepId !== 'navbar-profile-trigger') {
      return;
    }

    this.setStep('user-settings-friend-code');
  }

  completeAddContactTutorial() {
    if (!this.isAddContactTutorialActive || this.state.stepId !== 'user-settings-friend-code') {
      return;
    }

    this.completeActiveTutorial('add-contact');
  }

  private completeActiveTutorial(tutorialId: TutorialId) {
    this.resetComponentGuideContext();
    this.state = {
      tutorialId: null,
      stepId: null,
      justCompletedTutorialId: tutorialId,
    };
  }
}
